import React from 'react';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, Typography } from '@/components/common';
import { InputText } from '@/components/form';
import { FieldValues, useForm } from 'react-hook-form';
import Icons from '@/const/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { GET_LAWYER_NOTES } from '@/api';
import { casesLawyerService } from '@/api/casesLawyer';
import { NotesResponse } from '@/interfaces';
import { formatErrorMessage } from '@/lib';
import { formatDateToUS } from '@/utils';
import { useNotification } from '@/hooks';
import { CaseContext } from '@/context/CaseContext';

interface Props {
	caseId: string;
}

export const Notes: React.FC<Props> = ({ caseId }) => {
	const { toastError } = useNotification();
	const { isCaseReadOnly } = React.useContext(CaseContext);
	const { register, handleSubmit, reset } = useForm();

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<NotesResponse> => {
		const response = await casesLawyerService.getNotes(caseId, pageParam);
		return response.data;
	};

	const {
		data: notesResponse,
		fetchNextPage,
		hasNextPage,
		refetch,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_LAWYER_NOTES, caseId],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		refetchOnWindowFocus: false,
	});

	const handleScroll = (e: any): void => {
		const bottom =
			e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
		if (bottom && hasNextPage) {
			fetchNextPage();
		}
	};

	const onSubmit = async (value: FieldValues) => {
		try {
			await casesLawyerService.createNote(caseId, value.message);
			refetch();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			reset();
		}
	};

	const messages = notesResponse?.pages[0].items ?? [];

	return (
		<Accordion
			type="single"
			collapsible
			className="w-full border-2 border-green-base rounded-[32px]"
			defaultValue="item-1"
		>
			<AccordionItem value="item-1" className="border-none">
				<AccordionTrigger className="rounded-[32px] border-b border-grey-20 py-8 pl-14 pr-10 bg-green-secondary !no-underline">
					<Typography type="semibold-16">Notes</Typography>
				</AccordionTrigger>
				<AccordionContent className="pb-0">
					<div
						onScroll={handleScroll}
						className="space-y-6 p-6 overflow-y-auto scroll-custom max-h-[400px]"
					>
						{messages.length === 0 && (
							<div className="h-[300px] flex flex-col justify-center items-center">
								<Typography type="regular-18">
									There are currently no notes.
								</Typography>
							</div>
						)}

						{messages.length > 0 &&
							messages.map((item) => (
								<div
									key={item.id}
									className="flex items-center gap-3 px-3 relative w-full"
								>
									<Avatar
										photoUrl={item.user.profile_picture || ''}
										size="medium"
										firstname={item.user.firstname}
										lastname={item.user.lastname}
									/>
									<div className="space-y-2 w-full">
										<div className="flex items-center justify-between w-full">
											<Typography type="semibold-12">
												{`${item.user.firstname} ${item.user.lastname}`}
											</Typography>
											<Typography type="regular-12" className="!text-[#6E6A73]">
												{formatDateToUS(item.createdAt)}
											</Typography>
										</div>
										<Typography type="regular-12" className="opacity-60">
											{item.comment}
										</Typography>
									</div>
								</div>
							))}
					</div>
					<div className="bg-gray-15 rounded-b-[32px] p-4 border-t">
						<form onSubmit={handleSubmit(onSubmit)}>
							<InputText
								name="message"
								register={register}
								classNameInput="h-9"
								iconRight={Icons.send}
								rightClick={() => handleSubmit(onSubmit)()}
								classNameIcon="text-green-60 !top-[35%]"
								disabled={isCaseReadOnly}
							/>
						</form>
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
};
